import { QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import { Activity } from 'lucide-react';
import { lazy, StrictMode, Suspense, useEffect, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { NotSignedIn, signIn } from './api/client.js';
import { useMe } from './api/hook.js';
import { Shell } from './layout/Shell.js';
import { TooltipLayer } from './component/floating.js';
import { OverviewPage } from './page/Overview.js';
import { MapPage } from './page/Map.js';
import { ReleaseUpdateGuard } from './release/release.js';
import './style/token.css';
import './style/base.css';
import './style/layout.css';
import './style/component.css';

// Overview is in the main bundle (first screen); every other page loads on demand.
const AlertPage = lazy(() => import('./page/Alert.js').then((module) => ({ default: module.AlertPage })));
const ApplicationDetailPage = lazy(() => import('./page/ApplicationDetail.js').then((module) => ({ default: module.ApplicationDetailPage })));
const ApplicationPage = lazy(() => import('./page/Application.js').then((module) => ({ default: module.ApplicationPage })));
const CostPage = lazy(() => import('./page/Cost.js').then((module) => ({ default: module.CostPage })));
const ErrorDetailPage = lazy(() => import('./page/ErrorDetail.js').then((module) => ({ default: module.ErrorDetailPage })));
const ErrorPage = lazy(() => import('./page/Error.js').then((module) => ({ default: module.ErrorPage })));
const HealthPage = lazy(() => import('./page/Health.js').then((module) => ({ default: module.HealthPage })));
const LogPage = lazy(() => import('./page/Log.js').then((module) => ({ default: module.LogPage })));
const ProviderPage = lazy(() => import('./page/Provider.js').then((module) => ({ default: module.ProviderPage })));
const ReleaseNotePage = lazy(() => import('./page/ReleaseNote.js').then((module) => ({ default: module.ReleaseNotePage })));
const DataPage = lazy(() => import('./page/Data.js').then((module) => ({ default: module.DataPage })));
const DataStorePage = lazy(() => import('./page/DataStore.js').then((module) => ({ default: module.DataStorePage })));
const BackupPage = lazy(() => import('./page/Backup.js').then((module) => ({ default: module.BackupPage })));
const QueryPage = lazy(() => import('./page/Query.js').then((module) => ({ default: module.QueryPage })));
const QuotaPage = lazy(() => import('./page/Quota.js').then((module) => ({ default: module.QuotaPage })));
const SegnalazionePage = lazy(() => import('./page/Segnalazione.js').then((module) => ({ default: module.SegnalazionePage })));
const HostPage = lazy(() => import('./page/Host.js').then((module) => ({ default: module.HostPage })));
const RepositoryPage = lazy(() => import('./page/Repository.js').then((module) => ({ default: module.RepositoryPage })));
const LeadRecoveryPage = lazy(() => import('./page/LeadRecovery.js').then((module) => ({ default: module.LeadRecoveryPage })));
const TestPage = lazy(() => import('./page/Test.js').then((module) => ({ default: module.TestPage })));
const UsagePage = lazy(() => import('./page/Usage.js').then((module) => ({ default: module.UsagePage })));

const client = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: (count, error) => !(error instanceof NotSignedIn) && count < 2,
    },
  },
});

/** Before sign-in the shell is not shown: Security decides who enters, Monitor only asks. */
function Gate({ children }: { children: ReactNode }) {
  const me = useMe();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (me.error instanceof NotSignedIn) signIn();
  }, [me.error]);
  useEffect(() => {
    // Any query that finds the session gone sends the user back to Security.
    return queryClient.getQueryCache().subscribe((event) => {
      if (event.type === 'updated' && event.query.state.error instanceof NotSignedIn) signIn();
    });
  }, [queryClient]);
  if (me.data) return <>{children}</>;
  const ssoError = new URLSearchParams(location.search).get('sso_error');
  return (
    <div className="splash">
      <div className="splash-inner">
        <span className="brand-mark" style={{ width: 40, height: 40 }}>
          <Activity size={20} />
        </span>
        {me.isError && !(me.error instanceof NotSignedIn) ? (
          <>
            <strong>Monitor non raggiungibile</strong>
            <span className="muted">{me.error instanceof Error ? me.error.message : ''}</span>
            <button type="button" className="btn" onClick={() => me.refetch()}>
              Riprova
            </button>
          </>
        ) : ssoError ? (
          <>
            <strong>Accesso non riuscito ({ssoError})</strong>
            <button type="button" className="btn primary" onClick={() => signIn('/overview')}>
              Accedi con Security
            </button>
          </>
        ) : (
          <span>Verifico l’accesso con Security…</span>
        )}
      </div>
    </div>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <Gate>
          <Suspense fallback={<div className="splash">Carico…</div>}>
          <Routes>
            <Route element={<Shell />}>
              <Route index element={<Navigate to="/overview" replace />} />
              <Route path="overview" element={<OverviewPage />} />
              <Route path="map" element={<MapPage />} />
              <Route path="cost" element={<CostPage />} />
              <Route path="application" element={<ApplicationPage />} />
              <Route path="application/:id" element={<ApplicationDetailPage />} />
              <Route path="error" element={<ErrorPage />} />
              <Route path="error/:id" element={<ErrorDetailPage />} />
              <Route path="log" element={<LogPage />} />
              <Route path="health" element={<HealthPage />} />
              <Route path="test" element={<TestPage />} />
              <Route path="usage" element={<UsagePage />} />
              <Route path="quota" element={<QuotaPage />} />
              <Route path="query" element={<QueryPage />} />
              <Route path="query/:id" element={<QueryPage />} />
              <Route path="data" element={<DataPage />} />
              <Route path="data/:id" element={<DataStorePage />} />
              <Route path="backup" element={<BackupPage />} />
              <Route path="host" element={<HostPage />} />
              <Route path="repository" element={<RepositoryPage />} />
              <Route path="alert" element={<AlertPage />} />
              <Route path="segnalazione" element={<SegnalazionePage />} />
              <Route path="segnalazione/:id" element={<SegnalazionePage />} />
              <Route path="lead-recovery" element={<LeadRecoveryPage />} />
              <Route path="lead-recovery/:id" element={<LeadRecoveryPage />} />
              <Route path="provider" element={<ProviderPage />} />
              <Route path="release" element={<ReleaseNotePage />} />
              <Route path="*" element={<div className="page empty">Pagina non trovata.</div>} />
            </Route>
          </Routes>
          </Suspense>
        </Gate>
      </BrowserRouter>
      <ReleaseUpdateGuard />
      <TooltipLayer />
    </QueryClientProvider>
  </StrictMode>,
);
